import cloneDeep from 'clone-deep'
import { useProductTypeOptions } from 'hooks/useProductTypeOptions'
import { aiPromptInputs } from 'pages/Admin/AdminTools/Configuration/LoanSubmission/constants'
import { useEffect, useMemo, useState } from 'react'
import { getLoanTypeMapForSelect } from 'reducers/loanDetail.reducer'
import { getAiPromptTemplates } from 'services'
import { getAiProjects } from 'services/apis/aiProjects'
import { FormTable } from 'stories/components'
import { openConfigFieldAuditLog } from 'utils'

const getSelectedOptionText = (selected: Record<string, boolean> | undefined, options: Record<string, string>) => {
  if (!selected) return '-'
  const keys = Object.keys(selected).filter((key) => selected[key] !== false)
  if (!keys.length) return '-'
  return keys.map((key) => options[key] || key).join(', ')
}

const AI_PROMPT_HISTORY_FIELDS = [
  'templateId',
  'projectId',
  'prompt',
  'channels',
  'productTypes',
  'noRepeat',
  'visibleLogic',
] as const

export function WorkflowAiPromptsTable({
  aiPrompts,
  onChange,
  fieldKeyPrefix,
  actionTitle,
}: {
  aiPrompts: Array<any>
  onChange: (prompts: Array<any>) => void
  fieldKeyPrefix: string
  actionTitle: string
}) {
  const [rows, setRows] = useState<Array<any>>(aiPrompts || [])
  const [aiPromptTemplates, setAiPromptTemplates] = useState<any[]>([])
  const [aiProjects, setAiProjects] = useState<any[]>([])
  const [aInputs, setAInputs] = useState<Record<string, any>>({})

  useEffect(() => {
    setRows(aiPrompts || [])
  }, [aiPrompts])

  useEffect(() => {
    Promise.all([getAiPromptTemplates(), getAiProjects()]).then(([templatesRes, projects]) => {
      setAiPromptTemplates(templatesRes?.data || [])
      setAiProjects(projects || [])
    })
  }, [])

  useEffect(() => {
    const next = cloneDeep(aiPromptInputs())
    const templateOptions: Record<number, string> = {}
    aiPromptTemplates.forEach((template) => {
      templateOptions[template.id] = template.title
    })
    next.templateId.options = templateOptions

    const projectOptions: Record<number, string> = {}
    aiProjects.forEach((project) => {
      projectOptions[project.id] = project.name
    })
    next.projectId.options = projectOptions
    AI_PROMPT_HISTORY_FIELDS.forEach((key) => {
      if (next[key]) next[key].history = true
    })
    setAInputs(next)
  }, [aiPromptTemplates, aiProjects])

  const showFieldHistory = (id: number, key: string) => {
    if (!id) return
    const title = (aInputs[key] as any)?.title || key
    openConfigFieldAuditLog(
      `${fieldKeyPrefix}|${id}|${key}`,
      `${actionTitle} · AI Prompt #${id} · ${title}`,
      2,
    )
  }

  const getAiPromptTemplateTitle = (templateId: number) =>
    aiPromptTemplates.find((template) => template.id == templateId)?.title || templateId

  const getAiProjectName = (projectId?: number) =>
    projectId ? aiProjects.find((project) => project.id == projectId)?.name || projectId : '- From template -'

  const inputAiLogic = (inputs: Record<string, any>, _ID?: number, key?: string) => {
    if (key === 'templateId') {
      aiPromptTemplates.forEach((template) => {
        if (template.id == inputs.templateId.value) {
          inputs.prompt.value = template.prompt || ''
          if (template.projectId) inputs.projectId.value = template.projectId
        }
      })
    }
    if (inputs.noRepeat.value === true) inputs.noRepeat.value = 'single'
    if ([undefined, false].includes(inputs.noRepeat.value)) inputs.noRepeat.value = 'always'
    return inputs
  }

  const commitRows = (next: Array<any>) => {
    setRows(next)
    onChange(next)
  }

  const onSubmitAiPrompts = (currentId: any, values: Record<string, any>) => {
    values.noRepeat = values.noRepeat === 'single'
    return new Promise(async (resolve) => {
      const temp = cloneDeep(rows)
      if (!currentId) {
        let ID = 1
        rows.forEach((row) => {
          if (ID <= row.id) ID = row.id + 1
        })
        temp.push({ id: ID, ...values })
      } else {
        temp.forEach((item, index) => {
          if (item.id == currentId) temp[index] = { ...temp[index], ...values }
        })
      }
      commitRows(temp)
      resolve(values)
    })
  }

  const onRemoveAiPrompts = async (id: any) => {
    return new Promise(async (resolve) => {
      commitRows(rows.filter((item) => item.id != id))
      resolve(true)
    })
  }

  const channelOptions = useMemo(() => getLoanTypeMapForSelect(), [])
  const { optionsMap: productOptions } = useProductTypeOptions()

  if (!Object.keys(aInputs).length) return null

  return (
    <div className="mt-4 bg-slate-50 p-3 rounded-md">
      <div className="text-sm font-semibold text-shade-blue mb-2">- AI Prompts</div>
      <FormTable
        key="workflow-aiPrompts"
        header={[
          { key: 'id', title: 'No', value: (row: any) => row.id },
          {
            key: 'templateId',
            title: (
              <div>
                <div className="border-b mb-1">Template</div>
                <div>AI Project</div>
              </div>
            ),
            value: (row: any) => (
              <div>
                <div className="border-b mb-1">{getAiPromptTemplateTitle(row.templateId)}</div>
                <div>{getAiProjectName(row.projectId)}</div>
              </div>
            ),
          },
          {
            key: 'prompt',
            title: 'Prompt',
            value: (row: any) => <div className="max-w-md truncate whitespace-pre-wrap">{row.prompt || '-'}</div>,
          },
          {
            key: 'filters',
            title: (
              <div>
                <div className="border-b mb-1">Channel</div>
                <div>Product Type</div>
              </div>
            ),
            value: (row: any) => (
              <div>
                <div className="border-b mb-1">{getSelectedOptionText(row.channels, channelOptions)}</div>
                <div>{getSelectedOptionText(row.productTypes, productOptions)}</div>
              </div>
            ),
          },
        ]}
        inputs={aInputs}
        inputLogic={inputAiLogic}
        data={rows}
        onSubmit={(id, values) => onSubmitAiPrompts(id, values)}
        onRemove={(id) => onRemoveAiPrompts(id)}
        showFieldHistory={showFieldHistory}
        noText="No"
      />
    </div>
  )
}
